import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import WorkReportModal from './WorkReportModal';

const METRICS_POLL_MS = 60000;
const WIDGET_POS_KEY = 'workReportWidgetPosition';
const DRAG_THRESHOLD_PX = 8;

const WorkReportWidget = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activityCount, setActivityCount] = useState(null);
  const [activityIndicator, setActivityIndicator] = useState('green');
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(null);

  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({
    dragging: false,
    moved: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    pointerId: null,
  });

  const { currentUser } = useAuth();

  useEffect(() => {
    try {
      const raw = localStorage.getItem(WIDGET_POS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
          setOffset({ x: parsed.x, y: parsed.y });
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  const fetchRealTimeMetrics = useCallback(async () => {
    if (!currentUser) {
      return;
    }

    setMetricsError(null);
    setMetricsLoading(true);

    try {
      const token = await currentUser.getIdToken();
      if (!token) {
        setMetricsError('Not signed in');
        setActivityCount(null);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/work-report/realtime-metrics`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const text = await response.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        setMetricsError('Invalid response from server');
        setActivityCount(null);
        return;
      }

      if (!response.ok) {
        const msg = data.error || data.message || `${response.status} ${response.statusText}`;
        setMetricsError(typeof msg === 'string' ? msg : 'Could not load metrics');
        setActivityCount(null);
        return;
      }

      if (data.error) {
        setMetricsError(typeof data.error === 'string' ? data.error : 'Could not load metrics');
        setActivityCount(null);
        return;
      }

      const total = data.total_communications ?? 0;
      setActivityCount(total);

      if (total > 50) {
        setActivityIndicator('green');
      } else if (total > 20) {
        setActivityIndicator('yellow');
      } else {
        setActivityIndicator('red');
      }
    } catch (err) {
      setMetricsError(err.message || 'Network error');
      setActivityCount(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      return undefined;
    }
    fetchRealTimeMetrics();
    const interval = setInterval(fetchRealTimeMetrics, METRICS_POLL_MS);
    return () => clearInterval(interval);
  }, [currentUser, fetchRealTimeMetrics]);

  const endDrag = useCallback(() => {
    const d = dragRef.current;
    d.dragging = false;
    d.pointerId = null;
    setIsDragging(false);
    setOffset((cur) => {
      try {
        localStorage.setItem(WIDGET_POS_KEY, JSON.stringify(cur));
      } catch {
        /* ignore */
      }
      return cur;
    });
  }, []);

  useEffect(() => {
    const onUp = () => {
      if (dragRef.current.dragging) {
        endDrag();
      }
    };
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [endDrag]);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    dragRef.current.dragging = true;
    setIsDragging(true);
    dragRef.current.moved = false;
    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.originX = offset.x;
    dragRef.current.originY = offset.y;
    dragRef.current.pointerId = e.pointerId;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX) {
      dragRef.current.moved = true;
    }
    setOffset({
      x: dragRef.current.originX + dx,
      y: dragRef.current.originY + dy,
    });
  };

  const onPointerUp = (e) => {
    if (!dragRef.current.dragging) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const didDrag = dragRef.current.moved;
    endDrag();
    if (!didDrag) {
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (!currentUser) {
    return null;
  }

  const ringClass =
    activityIndicator === 'green'
      ? 'from-amber-500 to-orange-600 shadow-amber-500/40'
      : activityIndicator === 'yellow'
        ? 'from-amber-400 to-amber-600 shadow-amber-400/35'
        : 'from-orange-700 to-red-700 shadow-red-500/30';

  const statusDotClass =
    metricsError
      ? 'bg-gray-400'
      : metricsLoading
        ? 'bg-amber-300 animate-pulse'
        : activityIndicator === 'green'
          ? 'bg-emerald-500 shadow-emerald-500/60'
          : activityIndicator === 'yellow'
            ? 'bg-amber-400 shadow-amber-400/60'
            : 'bg-red-500 shadow-red-500/60 animate-pulse';

  return (
    <>
      <div
        className={`fixed bottom-5 right-5 md:bottom-8 md:right-8 z-50 touch-none select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px)`,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        title="Drag to move · Click (without dragging) for reports"
      >
        <div
          className={`relative w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center transition-transform duration-300 border-2 border-white/30 shadow-lg hover:scale-105 active:scale-95 bg-gradient-to-br ${ringClass}`}
        >
          <img
            src="/hero-character.jpeg"
            alt="Alice - Work Report Agent"
            className="w-full h-full object-cover rounded-full pointer-events-none"
            draggable={false}
          />
          {!metricsLoading && activityCount !== null && activityCount > 0 && (
            <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2 bg-orange-600 text-white rounded-full w-5 h-5 md:w-6 md:h-6 flex items-center justify-center text-xs font-bold border-2 border-white animate-pulse pointer-events-none">
              {activityCount > 99 ? '99+' : activityCount}
            </div>
          )}
          <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-white flex items-center justify-center border-2 border-black/10 pointer-events-none">
            <div className={`w-2 h-2 rounded-full transition-all duration-300 ${statusDotClass}`} />
          </div>
        </div>
      </div>

      {isModalOpen && (
        <WorkReportModal isOpen={isModalOpen} onClose={handleCloseModal} user={currentUser} />
      )}
    </>
  );
};

export default WorkReportWidget;
